import { useId } from 'react'
import { Check } from 'lucide-react'

import styles from './AiRulesSettings.module.css'

export type VoiceChoice<Key extends string> = {
  key: Key
  name: string
  description: string
}

/**
 * One voice setting as native radios: compact cards (speakers) or a segmented
 * row (pace, background) that describes the selected option underneath.
 * "Current" marks what the live assistant uses, when that is known.
 */
export function VoiceChoiceGroup<Key extends string>({
  legend,
  name,
  options,
  value,
  current,
  disabled,
  variant,
  testId,
  onChange,
}: {
  legend: string
  name: string
  options: Array<VoiceChoice<Key>>
  value: Key
  current: Key | null
  disabled: boolean
  variant: 'cards' | 'segmented'
  testId?: string
  onChange: (key: Key) => void
}) {
  const describedId = useId()
  const selected = options.find((option) => option.key === value)
  return (
    <fieldset
      className={styles.choiceGroup}
      data-variant={variant}
      disabled={disabled}
    >
      <legend>{legend}</legend>
      <div className={styles.choiceOptions} data-testid={testId}>
        {options.map((option) => {
          const isSelected = option.key === value
          const isCurrent = option.key === current
          return (
            <label
              key={option.key}
              className={styles.choice}
              data-selected={isSelected || undefined}
              data-current={isCurrent}
            >
              <input
                className={styles.choiceInput}
                type="radio"
                name={name}
                value={option.key}
                checked={isSelected}
                onChange={() => onChange(option.key)}
              />
              <span className={styles.choiceName}>
                {option.name}
                {isCurrent ? (
                  <em className={styles.currentBadge}>Current</em>
                ) : null}
              </span>
              {variant === 'cards' ? (
                <small className={styles.choiceDescription}>
                  {option.description}
                </small>
              ) : (
                <span className={styles.srOnlyText}>{option.description}</span>
              )}
              {variant === 'cards' ? (
                <span className={styles.choiceCheck} aria-hidden="true">
                  {isSelected ? <Check size={11} /> : null}
                </span>
              ) : null}
            </label>
          )
        })}
      </div>
      {variant === 'segmented' && selected ? (
        <p id={describedId} className={styles.choiceHint}>
          {selected.description}
        </p>
      ) : null}
    </fieldset>
  )
}
